import { Button } from 'primereact/button'
import { FileUpload } from 'primereact/fileupload'
import { ProgressBar } from 'primereact/progressbar'
import { Tag } from 'primereact/tag'
import { Tooltip } from 'primereact/tooltip'
import React, { useMemo, useRef, useState } from 'react'
import { notificationDanger, notificationInfo } from '../../utils/customToastify'

const DEFAULT_MAX_FILE_SIZE = 5 * 1000 * 1000 // 5 MB
const MAX_ATTACHMENT_FILES = 5
export const AddAttachment = ({
  multiple,
  onUploadDocuments,
  maxAttachmentFiles = MAX_ATTACHMENT_FILES,
  maxFileSize = DEFAULT_MAX_FILE_SIZE,
  accept = '*/*',
}) => {
  const fileUploadRef = useRef()

  const [totalSize, setTotalSize] = useState(0)
  const [numberOfFiles, setNumberOfFiles] = useState(0)

  const onTemplateSelect = (e) => {
    if (!multiple && numberOfFiles > 1) {
      notificationDanger('Only one file is allowed')
      return
    }

    setNumberOfFiles((prev) => prev + e.files.length)
    let _totalSize = totalSize
    let files = e.files

    Object.keys(files).forEach((key) => {
      if (files[key].size <= maxFileSize && files[key].size > 0) {
        _totalSize += files[key].size || 0
      }
    })

    setTotalSize(_totalSize)
  }

  const validateNumberOfUploads = (event) => {
    const currentFiles = event.files

    return currentFiles.length <= maxAttachmentFiles
  }

  const onTemplateUpload = async (e) => {
    if (!validateNumberOfUploads(e)) {
      notificationDanger(`You can only upload up to ${maxAttachmentFiles} files.`)
      return
    }
    onUploadDocuments(e.files)

    let _totalSize = 0

    e.files.forEach((file) => {
      _totalSize += file.size || 0
    })

    setTotalSize(_totalSize)
    notificationInfo('File(s) Will Be Uploaded On Action Done')

    // After the upload is complete, clear the file list:
    if (fileUploadRef.current) {
      fileUploadRef.current.clear()
    }
  }

  const onTemplateRemove = (file, callback) => {
    setTotalSize((prev) => prev - (file.size ?? 0))
    setNumberOfFiles((prev) => prev - 1)
    if (callback && typeof callback === 'function') callback()
  }

  const onTemplateClear = () => {
    setTotalSize(0)
    setNumberOfFiles(0)
  }

  const headerTemplate = (options) => {
    const { className, chooseButton, uploadButton, cancelButton } = options
    const value = totalSize / 1000000

    const formatedValue = ((totalSize || 0) / 1000000).toFixed(2)

    return (
      <div className={className} style={{ backgroundColor: 'transparent', display: 'flex', alignItems: 'center' }}>
        {chooseButton}
        {uploadButton}
        {multiple && cancelButton}
        <div className='d-flex align-items-center gap-3 ml-auto'>
          <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {formatedValue} / {maxFileSize / 1000000} MB
          </span>
          <ProgressBar value={value} showValue={false} style={{ width: '10rem', height: '12px' }}></ProgressBar>
        </div>
      </div>
    )
  }

  const itemTemplate = (file, props) => {
    return (
      <div className='d-flex align-items-center'>
        <div className='d-flex align-items-center' style={{ flex: 1 }}>
          <img alt={'file'} role='presentation' src={file.objectURL} width={100} />
          <span className='d-flex flex-column text-left ml-3'>
            {file.name}
            <small>{new Date().toLocaleDateString()}</small>
          </span>
        </div>
        <div className='d-flex align-items-center'>
          {multiple && (
            <Tag value={((file.size || 0) / 1000000).toFixed(2) + ' MB'} severity='warning' className='p-2 mr-2' />
          )}
          <Button
            type='button'
            icon='pi pi-times'
            className='p-button-outlined p-button-rounded p-button-danger ml-auto'
            onClick={() => onTemplateRemove(file, props.onRemove)}
          />
        </div>
      </div>
    )
  }

  const emptyTemplate = useMemo(() => {
    return (
      <div className='d-flex align-items-center justify-content-center flex-column'>
        <i
          className='pi pi-file p-5'
          style={{
            fontSize: '5em',
            borderRadius: '50%',
            backgroundColor: 'var(--surface-b)',
            color: 'var(--surface-d)',
          }}
        ></i>
        <span
          style={{
            fontSize: '1.2em',
            color: 'var(--text-color-secondary)',
            textAlign: 'center',
            display: 'block',
          }}
          className='my-5'
        >
          Drag and Drop File Here
        </span>
      </div>
    )
  }, [])

  const options = useMemo(() => {
    return {
      choose: {
        style: { display: !multiple && totalSize ? 'none' : 'flex' },
        icon: 'pi pi-fw pi-images',
        iconOnly: true,
        className: 'custom-choose-btn p-button-rounded p-button-outlined',
      },
      upload: {
        icon: 'pi pi-fw pi-cloud-upload',
        iconOnly: true,
        className: 'custom-upload-btn p-button-success p-button-rounded p-button-outlined',
      },
      cancel: {
        icon: 'pi pi-fw pi-times',
        iconOnly: true,
        className: 'custom-cancel-btn p-button-danger p-button-rounded p-button-outlined',
      },
    }
  }, [totalSize])

  return (
    <div>
      <Tooltip target='.custom-choose-btn' content='Select File' position='bottom' />
      <Tooltip target='.custom-upload-btn' content='Upload' position='bottom' />
      <Tooltip target='.custom-cancel-btn' content='Remove' position='bottom' />

      <FileUpload
        ref={fileUploadRef}
        name='attachment'
        // url='/api/upload'
        mode='advanced'
        accept={accept}
        customUpload
        multiple={multiple}
        maxFileSize={maxFileSize}
        maxFileCount={maxAttachmentFiles}
        // onUpload={onTemplateUpload}
        uploadHandler={onTemplateUpload}
        onSelect={onTemplateSelect}
        onError={onTemplateClear}
        onClear={onTemplateClear}
        onRemove={onTemplateRemove}
        headerTemplate={headerTemplate}
        itemTemplate={itemTemplate}
        emptyTemplate={emptyTemplate}
        chooseOptions={options.choose}
        uploadOptions={options.upload}
        cancelOptions={options.cancel}
      />
    </div>
  )
}
